import React from 'react'
import { getChallengeCover, COVER_MOTIF_COUNT } from '@/utils/challengeCover.utils'
import { useCoverObjectPositionY } from '@/hooks/useCoverObjectPositionY'

interface CoverArtProps {
  taskId: string
  coverImageUrl?: string
  alt: string
  className?: string
  // Thumbnail-scale surfaces pass 'sm' to load the smaller cover variant.
  size?: 'sm' | 'lg'
  // Vertical focal point (0-100) for the cover image, applied as the CSS
  // object-position Y so admins control which slice shows in the clipped area.
  // Only affects real cover images, not the generated gradient/motif.
  focusY?: number
}

// Six abstract, data-themed motifs (chart, scatter, network, wave, grid, pie).
// Decorative only — kept LTR. Rendered semi-transparent over the gradient.
const Motif = ({ index }: { index: number }) => {
  const i = ((index % COVER_MOTIF_COUNT) + COVER_MOTIF_COUNT) % COVER_MOTIF_COUNT
  const common = { fill: 'none', stroke: 'rgba(255,255,255,0.55)', strokeWidth: 2 } as const
  const paths = [
    <g key="bars"><rect x="10" y="40" width="10" height="22" {...common} /><rect x="26" y="28" width="10" height="34" {...common} /><rect x="42" y="16" width="10" height="46" {...common} /></g>,
    <g key="scatter"><circle cx="16" cy="44" r="4" {...common} /><circle cx="30" cy="28" r="4" {...common} /><circle cx="46" cy="36" r="4" {...common} /><circle cx="54" cy="18" r="4" {...common} /></g>,
    <g key="network"><circle cx="16" cy="40" r="5" {...common} /><circle cx="44" cy="20" r="5" {...common} /><circle cx="50" cy="48" r="5" {...common} /><path d="M20 38 L40 22 M20 42 L46 46" {...common} /></g>,
    <g key="wave"><path d="M8 40 Q20 20 32 40 T56 40" {...common} /></g>,
    <g key="grid"><path d="M14 14 H54 M14 30 H54 M14 46 H54 M14 14 V54 M30 14 V54 M46 14 V54" {...common} /></g>,
    <g key="pie"><circle cx="34" cy="34" r="18" {...common} /><path d="M34 34 L34 16 M34 34 L50 40" {...common} /></g>,
  ]
  return (
    <svg viewBox="0 0 64 64" className="h-14 w-14 opacity-80 drop-shadow" aria-hidden="true">
      {paths[i]}
    </svg>
  )
}

export const CoverArt = ({ taskId, coverImageUrl, alt, className, size, focusY }: CoverArtProps) => {
  const cover = getChallengeCover({ taskId, coverImageUrl, size })
  const { containerRef, imgRef, onImgLoad, objectPositionY } = useCoverObjectPositionY(focusY)
  return (
    <div ref={containerRef} className={`relative overflow-hidden ${className ?? ''}`} style={{ background: cover.gradient }}>
      {cover.imageUrl !== undefined ? (
        <img ref={imgRef} onLoad={onImgLoad} src={cover.imageUrl} alt={alt} className="absolute inset-0 h-full w-full object-cover" loading="lazy" style={{ objectPosition: `50% ${objectPositionY}%` }} />
      ) : (
        <div className="absolute inset-0 grid place-items-center">
          <Motif index={cover.motifIndex} />
        </div>
      )}
      {/* Legibility scrim so overlaid chips/titles stay readable on any cover. */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-black/55" />
    </div>
  )
}
